// ConsoleDashboard.tsx
// Main container for the Dashboard console page.
// Owns all state, data fetching, and handlers.
// Rendering is delegated to section components:
//   - DashboardSelectorCard
//   - DashboardApprovalCard
//   - DashboardSpecForm
//   - SqlValidationCard

import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import ConsoleLayout from '../ConsoleLayout';
import { useAuth } from '../../../context/AuthContext';
import { consoleDashboardsApi } from '../../../services/api/console/dashboards.api';
import { consoleTablesApi } from '../../../services/api/console/tables.api';
import {
  ConsoleDashboardDescriptionSections,
  ConsoleDashboardDetail,
  ConsoleManagedTable,
  ConsoleDashboardModelRow,
  ConsoleDashboardSummary,
  DashboardSqlValidationResult,
  ColumnsDerivedGenerationResult,
} from '../../../types';

import { ModelRowState, ColumnsDerivedEntry, DEFAULT_DESCRIPTION_SECTIONS } from './types';
import { makeUid, parseEmails, parseCsv, wordCount, renderDescriptionFromSections, normalizeModelRows } from './utils';
import DashboardSelectorCard from './DashboardSelectorCard';
import DashboardApprovalCard from './DashboardApprovalCard';
import DashboardSpecForm from './DashboardSpecForm';
import SqlValidationCard from './SqlValidationCard';

// Errors that indicate a permission/access boundary — not actionable for the user, suppress silently.
const SILENT_ERROR_FRAGMENTS = [
  'does not have Console access',
  'role does not have',
  'not authorized',
  'unauthorized',
  'forbidden',
  'permission denied',
];

const isSilentError = (error: any): boolean => {
  const detail: string = error?.response?.data?.detail || '';
  return SILENT_ERROR_FRAGMENTS.some((fragment) =>
    detail.toLowerCase().includes(fragment.toLowerCase())
  );
};

const apiErrorToast = (error: any, fallback: string) => {
  if (isSilentError(error)) return;
  toast.error(error?.response?.data?.detail || fallback);
};

const ConsoleDashboard: React.FC = () => {
  const { user } = useAuth();
  const requesterEmail = user?.email || user?.userId || '';

  // ── Data state ───────────────────────────────────────────────────────────────
  const [dashboards, setDashboards] = useState<ConsoleDashboardSummary[]>([]);
  const [availableTables, setAvailableTables] = useState<ConsoleManagedTable[]>([]);
  const [tableColumnsById, setTableColumnsById] = useState<Record<number, string[]>>({});
  const [selectedDashboardId, setSelectedDashboardId] = useState('');
  const [loadingDashboards, setLoadingDashboards] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingSpec, setSavingSpec] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  const [savingAuthorizedUsers, setSavingAuthorizedUsers] = useState(false);
  const [indexingDashboard, setIndexingDashboard] = useState(false);

  const [syncingAccess, setSyncingAccess] = useState(false);

  const [savingModelRows, setSavingModelRows] = useState<Record<string, boolean>>({});
  const [deletingModelRows, setDeletingModelRows] = useState<Record<string, boolean>>({});

  // ── Dashboard spec form state ────────────────────────────────────────────────
  const [dashboardDetail, setDashboardDetail] = useState<ConsoleDashboardDetail | null>(null);
  const [name, setName] = useState('');
  const [descriptionSections, setDescriptionSections] = useState<ConsoleDashboardDescriptionSections>(
    DEFAULT_DESCRIPTION_SECTIONS
  );
  // filters_applicable is now a plain string on the API side
  const [filtersApplicableInput, setFiltersApplicableInput] = useState('');
  const [sourceLink, setSourceLink] = useState('');
  const [authorizedUsersInput, setAuthorizedUsersInput] = useState('');

  // ── Model state ──────────────────────────────────────────────────────────────
  const [modelRows, setModelRows] = useState<ModelRowState[]>([]);
  const [columnsDerivedByUid, setColumnsDerivedByUid] = useState<Record<string, ColumnsDerivedEntry[]>>({});
  // Stores the full generation API result per row _uid — used for display
  const [columnsDerivedResults, setColumnsDerivedResults] = useState<Record<string, ColumnsDerivedGenerationResult>>({});
  const [generatingColumnsByUid, setGeneratingColumnsByUid] = useState<Record<string, boolean>>({});

  // ── Temporal scope state ─────────────────────────────────────────────────────
  const [temporalScope, setTemporalScope] = useState<{
    start_year?: number | null;
    end_year?: number | null;
    is_rolling: boolean;
    current_year_only: boolean;
  }>({
    start_year: undefined,
    end_year: undefined,
    is_rolling: false,
    current_year_only: false,
  });
  const [mainYearColumn, setMainYearColumn] = useState('');
  const [mainYearColumnOptions, setMainYearColumnOptions] = useState<string[]>([]);
  const [loadingMainColumns, setLoadingMainColumns] = useState(false);
  const [derivingTemporalScope, setDerivingTemporalScope] = useState(false);

  // ── SQL validation state ─────────────────────────────────────────────────────
  const [sqlToValidate, setSqlToValidate] = useState('');
  const [validatingSql, setValidatingSql] = useState(false);
  const [sqlResult, setSqlResult] = useState<DashboardSqlValidationResult | null>(null);
  const [approvingStage, setApprovingStage] = useState<'developer' | 'admin' | ''>('');
  const [pushingToProd, setPushingToProd] = useState(false);

  // ── Derived/memoized values ──────────────────────────────────────────────────
  const selectedDashboard = useMemo(
    () => dashboards.find((item) => item.dashboard_id === selectedDashboardId) || null,
    [dashboards, selectedDashboardId]
  );

  const renderedDescription = useMemo(
    () => renderDescriptionFromSections(descriptionSections),
    [descriptionSections]
  );

  const descriptionWords = useMemo(() => {
    const { dashboard_purpose, cannot_answer, filters_applicable, what_it_answers, business_rules_logic } = descriptionSections;
    const combined = [
      dashboard_purpose,
      cannot_answer,
      filters_applicable || '',
      what_it_answers,
      business_rules_logic,
    ].join(' ');
    return wordCount(combined);
  }, [descriptionSections]);

  const mainModelRow = useMemo(() => modelRows[0], [modelRows]);
  const tableById = useMemo(() => {
    const mapping: Record<number, ConsoleManagedTable> = {};
    availableTables.forEach((table) => {
      mapping[table.table_id] = table;
    });
    return mapping;
  }, [availableTables]);

  const resolveTableIdFromReference = useCallback((row: ModelRowState, tableRef: string): number | null => {
    const cleanRef = String(tableRef || '').trim();
    if (!cleanRef) return null;
    for (const sourceTable of row.source_tables || []) {
      const tableId = sourceTable.table_id;
      const table = tableById[tableId];
      if (!table) continue;
      const postgresName = (table.postgres_table_name || '').trim().toLowerCase();
      const snowflakeName = (table.snowflakes_table_name || '').trim().toLowerCase();
      if (
        cleanRef === String(tableId) ||
        cleanRef.toLowerCase() === postgresName ||
        cleanRef.toLowerCase() === snowflakeName ||
        cleanRef.toLowerCase() === (sourceTable.table_name || '').toLowerCase()
      ) {
        return tableId;
      }
    }
    return null;
  }, [tableById]);

  // ── Hydrate form from API detail response ────────────────────────────────────
  const hydrateFromDetail = useCallback((detail: ConsoleDashboardDetail) => {
    setDashboardDetail(detail);
    setName(detail.name || '');

    const sectionPayload = detail.description_sections || {
      ...DEFAULT_DESCRIPTION_SECTIONS,
      dashboard_purpose: detail.description || '',
    };
    setDescriptionSections({
      dashboard_purpose: sectionPayload.dashboard_purpose || '',
      cannot_answer: sectionPayload.cannot_answer || '',
      // filters_applicable is now a string from the API
      filters_applicable: sectionPayload.filters_applicable || '',
      what_it_answers: sectionPayload.what_it_answers || '',
      business_rules_logic: sectionPayload.business_rules_logic || '',
    });
    // Keep filters input in sync with the string value
    setFiltersApplicableInput(sectionPayload.filters_applicable || '');

    setSourceLink(detail.source_link || '');
    setAuthorizedUsersInput((detail.authorized_users || []).join('\n'));

    // CHANGED: model rows now use source_tables instead of source_table_ids.
    // Preserve existing _uids by matching on view_name so per-row error state
    // in the form stays valid after a background re-fetch.
    const normalizedModel = normalizeModelRows(
      (detail.model || []).map((row, index) => ({
        _uid: makeUid(),
        role: index === 0 ? 'fact' : 'bridge',
        view_name: row.view_name || '',
        grain: row.grain || '',
        alias: row.alias || '',
        primary_key: row.primary_key || '',
        sql_view: row.sql_view || '',
        is_validated: row.is_validated,
        source_tables: (row.source_tables || []).map((st) => ({
          table_id: Number(st.table_id),
          table_name: st.table_name || '',
        })),
        join_to_fact: row.join_to_fact
          ? {
              fact_key: row.join_to_fact.fact_key || '',
              this_key: row.join_to_fact.this_key || '',
              type: row.join_to_fact.type || 'exists',
            }
          : undefined,
      }))
    );
    setModelRows(normalizedModel);

    // CHANGED: columns_derived is now { views: [...], is_validated }
    const derivedByUid: Record<string, ColumnsDerivedEntry[]> = {};
    normalizedModel.forEach((row) => {
      derivedByUid[row._uid] = [{ table_name: '', columns: [] }];
    });

    const detailViews = detail.columns_derived?.views || [];
    detailViews.forEach((viewEntry) => {
      const match = normalizedModel.find((row) => row.view_name === viewEntry.view_name);
      if (!match) return;
      const rows: ColumnsDerivedEntry[] = (viewEntry.tables || []).map((t) => ({
        table_name: t.table_name || '',
        columns: (t.columns || []).map((c) => String(c).trim()).filter(Boolean),
      }));
      derivedByUid[match._uid] = rows.length ? rows : [{ table_name: '', columns: [] }];
    });

    setColumnsDerivedByUid(derivedByUid);

    // Populate columnsDerivedResults from GET response so previously generated
    // views are displayed in tabular format without needing to re-generate
    const resultsFromDetail: Record<string, ColumnsDerivedGenerationResult> = {};
    detailViews.forEach((viewEntry) => {
      const match = normalizedModel.find((row) => row.view_name === viewEntry.view_name);
      if (!match) return;
      resultsFromDetail[match._uid] = {
        dashboard_id: detail.dashboard_id || '',
        view: {
          view_name: viewEntry.view_name,
          tables: (viewEntry.tables || []).map((t) => ({
            table_name: t.table_name || '',
            columns: (t.columns || []).map((col) =>
              typeof col === 'string'
                ? { view_column: col, source_column: col, is_mapped: true, column_metadata: null }
                : col
            ),
          })),
          manual_construction: (viewEntry as any).manual_construction || [],
        },
        is_validated: detail.columns_derived?.is_validated ?? false,
        updated_at: detail.updated_at || '',
        updated_by: detail.updated_by || '',
      };
    });
    setColumnsDerivedResults(resultsFromDetail);

    setTemporalScope({
      start_year: detail.temporal_scope?.start_year ?? undefined,
      end_year: detail.temporal_scope?.end_year ?? undefined,
      is_rolling: !!detail.temporal_scope?.is_rolling,
      current_year_only: !!detail.temporal_scope?.current_year_only,
    });
    setMainYearColumn('');
    setMainYearColumnOptions([]);

    setSqlResult(null);
    setSqlToValidate('');
  }, []);

  // ── Data fetching ────────────────────────────────────────────────────────────
  const loadDashboards = useCallback(async () => {
    if (!requesterEmail || !user?.canAccessConsole) return;
    setLoadingDashboards(true);
    try {
      const rows = await consoleDashboardsApi.listDashboards(requesterEmail);
      setDashboards(rows);

      if (!rows.length) {
        setSelectedDashboardId('');
        setDashboardDetail(null);
        return;
      }

      const stillValid = rows.some((item) => item.dashboard_id === selectedDashboardId);
      if (!stillValid) {
        setSelectedDashboardId('');
      }
    } catch (error: any) {
      apiErrorToast(error, 'Failed to load dashboards');
    } finally {
      setLoadingDashboards(false);
    }
  }, [requesterEmail, selectedDashboardId, user?.canAccessConsole]);

  const loadSourceTables = useCallback(async () => {
    if (!requesterEmail || !user?.canAccessConsole) return;
    try {
      const rows = await consoleTablesApi.listAvailableTables(requesterEmail);
      setAvailableTables(rows);
    } catch (error: any) {
      apiErrorToast(error, 'Failed to load table list');
    }
  }, [requesterEmail, user?.canAccessConsole]);

  const ensureTableColumnsLoaded = useCallback(async (tableId: number) => {
    if (!requesterEmail || !tableId) return;
    if (tableColumnsById[tableId]) return;
    try {
      const profile = await consoleTablesApi.getTableProfile(tableId, requesterEmail);
      const columns = (profile.table_profile || []).map((row) => row.column).filter(Boolean);
      setTableColumnsById((prev) => ({ ...prev, [tableId]: columns }));
    } catch (error: any) {
      apiErrorToast(error, `Failed to load columns for table #${tableId}`);
    }
  }, [requesterEmail, tableColumnsById]);

  const loadDashboardDetail = useCallback(async (dashboardId: string) => {
    if (!requesterEmail || !dashboardId) return;
    setLoadingDetail(true);
    try {
      const detail = await consoleDashboardsApi.getDashboardDetail(dashboardId, requesterEmail);
      hydrateFromDetail(detail);
    } catch (error: any) {
      apiErrorToast(error, 'Failed to load dashboard detail');
    } finally {
      setLoadingDetail(false);
    }
  }, [hydrateFromDetail, requesterEmail]);

  useEffect(() => {
    void loadDashboards();
  }, [loadDashboards]);

  useEffect(() => {
    void loadSourceTables();
  }, [loadSourceTables]);

  useEffect(() => {
    if (!selectedDashboardId) return;
    void loadDashboardDetail(selectedDashboardId);
  }, [selectedDashboardId, loadDashboardDetail]);

  // Load table columns only when Columns Derived entries reference a table.
  // We intentionally do NOT trigger this from modelRows source_tables changes
  // to avoid hitting the profile API when selecting tables in the Data Model section.
  useEffect(() => {
    const pendingIds = new Set<number>();
    Object.values(columnsDerivedByUid).forEach((entries) => {
      entries.forEach((entry) => {
        if (!entry.table_name) return;
        // Find the table_id by matching postgres_table_name across available tables
        const matched = availableTables.find(
          (t) => t.postgres_table_name === entry.table_name
        );
        if (matched && !tableColumnsById[matched.table_id]) {
          pendingIds.add(matched.table_id);
        }
      });
    });
    pendingIds.forEach((tableId) => {
      void ensureTableColumnsLoaded(tableId);
    });
  }, [columnsDerivedByUid, availableTables, tableColumnsById, ensureTableColumnsLoaded]);

  // ── Description section handler ──────────────────────────────────────────────
  const updateSection = (key: keyof ConsoleDashboardDescriptionSections, value: string | string[]) => {
    setDescriptionSections((prev) => ({
      ...prev,
      // filters_applicable is now always a string
      [key]: Array.isArray(value) ? value.join('\n') : value,
    }));
  };

  // ── Model row handlers ───────────────────────────────────────────────────────
  const updateModelRow = (uid: string, patch: Partial<ModelRowState>) => {
    setModelRows((prev) =>
      normalizeModelRows(
        prev.map((row) => {
          if (row._uid !== uid) return row;
          const nextRow: ModelRowState = {
             ...row,
              ...patch, 
            };

          // CHANGED: handle source_tables instead of source_table_ids
          if (patch.source_tables) {
            const seen = new Set<number>();
            nextRow.source_tables = patch.source_tables.filter((st) => {
              if (!Number.isFinite(st.table_id) || seen.has(st.table_id)) return false;
              seen.add(st.table_id);
              return true;
            }
          );
          }
          return nextRow;
        })
      )
    );

    // Sync columnsDerived when source_tables change
    if (patch.source_tables) {
      const selectedIds = new Set((patch.source_tables || []).map((st) => st.table_id));
      setColumnsDerivedByUid((prev) => {
        const existing = prev[uid] || [];
        const filtered = existing.filter((entry) => {
          const ref = (entry.table_name || '').trim();
          if (!ref) return true;
          const numericRef = Number(ref);
          if (Number.isFinite(numericRef) && selectedIds.has(numericRef)) return true;
          return Array.from(selectedIds).some((tableId) => {
            const table = tableById[tableId];
            if (!table) return false;
            return (
              ref.toLowerCase() === (table.postgres_table_name || '').toLowerCase() ||
              ref.toLowerCase() === (table.snowflakes_table_name || '').toLowerCase()
            );
          });
        });
        return {
          ...prev,
          [uid]: filtered.length ? filtered : [{ table_name: '', columns: [] }],
        };
      });
    }
  };

  const addBridgeRow = () => {
    const next = normalizeModelRows([
      ...modelRows,
      {
        _uid: makeUid(),
        role: 'bridge',
        view_name: '',
        grain: '',
        alias: '',
        primary_key: '',
        sql_view: '',
        source_tables: [],
        join_to_fact: { fact_key: '', this_key: '', type: 'exists' },
      },
    ]);
    setModelRows(next);
    const last = next[next.length - 1];
    setColumnsDerivedByUid((prev) => ({
      ...prev,
      [last._uid]: [{ table_name: '', columns: [] }],
    }));
  };

  const removeBridgeRow = (uid: string) => {
    const targetIndex = modelRows.findIndex((row) => row._uid === uid);
    if (targetIndex <= 0) return;
    setModelRows((prev) => normalizeModelRows(prev.filter((row) => row._uid !== uid)));
    setColumnsDerivedByUid((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });
  };

  // ── Columns derived handlers ─────────────────────────────────────────────────
  const updateDerivedEntry = (uid: string, index: number, patch: Partial<ColumnsDerivedEntry>) => {
    setColumnsDerivedByUid((prev) => ({
      ...prev,
      [uid]: (prev[uid] || []).map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    }));
  };

  const addDerivedEntry = (uid: string) => {
    setColumnsDerivedByUid((prev) => ({
      ...prev,
      [uid]: [...(prev[uid] || []), { table_name: '', columns: [] }],
    }));
  };

  const removeDerivedEntry = (uid: string, index: number) => {
    setColumnsDerivedByUid((prev) => {
      const current = prev[uid] || [];
      const nextRows = current.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        [uid]: nextRows.length ? nextRows : [{ table_name: '', columns: [] }],
      };
    });
  };

  // ── Temporal scope handlers ──────────────────────────────────────────────────
  // CHANGED: GET /console/dashboards/{id}/fact-columns (was POST preview-columns)
  const loadMainViewColumns = async () => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    setLoadingMainColumns(true);
    try {
      const columns = await consoleDashboardsApi.getDashboardFactColumns(selectedDashboardId, requesterEmail);
      setMainYearColumnOptions(columns);
      if (!columns.includes(mainYearColumn)) {
        setMainYearColumn(columns[0] || '');
      }
      toast.success(`Loaded ${columns.length} columns from main fact view`);
    } catch (error: any) {
      apiErrorToast(error, 'Failed to load fact columns');
    } finally {
      setLoadingMainColumns(false);
    }
  };

  // CHANGED: POST /console/dashboards/{id}/temporal-scope (was derive-temporal-scope)
  // Body is now just { requester_email, column_name }
  const deriveTemporalScope = async () => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    if (!mainYearColumn.trim()) {
      toast.error('Select year column first');
      return;
    }
    setDerivingTemporalScope(true);
    try {
      const result = await consoleDashboardsApi.deriveDashboardTemporalScope({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        column_name: mainYearColumn,
      });
      setTemporalScope({
        start_year: result.temporal_scope?.start_year ?? undefined,
        end_year: result.temporal_scope?.end_year ?? undefined,
        is_rolling: !!result.temporal_scope?.is_rolling,
        current_year_only: !!result.temporal_scope?.current_year_only,
      });
      toast.success(`Temporal scope generated from ${result.detected_year_count} year bucket(s)`);
    } catch (error: any) {
      apiErrorToast(error, 'Failed to derive temporal scope');
    } finally {
      setDerivingTemporalScope(false);
    }
  };

  // ── Save description sections handler ───────────────────────────────────────
  const handleSaveDescriptionSections = async () => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }

    // Validate each field is non-empty
    const emptyFields: string[] = [];
    if (!descriptionSections.dashboard_purpose.trim()) emptyFields.push('Dashboard Purpose');
    if (!descriptionSections.cannot_answer.trim()) emptyFields.push('What It Cannot Answer');
    if (!filtersApplicableInput.trim()) emptyFields.push('Filters That Can Be Applied');
    if (!descriptionSections.what_it_answers.trim()) emptyFields.push('What It Answers');
    if (!descriptionSections.business_rules_logic.trim()) emptyFields.push('Business Rules Logic');
    if (emptyFields.length) {
      return { valid: false, words: descriptionWords, emptyFields };
    }

    // Word count validation
    const words = descriptionWords;
    if (words < 200 || words > 1000) {
      return { valid: false, words, emptyFields: [] };
    }

    setSavingDescription(true);
    try {
      await consoleDashboardsApi.updateDashboardDescriptionSections({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        dashboard_purpose: descriptionSections.dashboard_purpose,
        cannot_answer: descriptionSections.cannot_answer,
        filters_applicable: filtersApplicableInput,
        what_it_answers: descriptionSections.what_it_answers,
        business_rules_logic: descriptionSections.business_rules_logic,
      });
      await loadDashboardDetail(selectedDashboardId);
      toast.success('Description sections saved');
      return { valid: true, words, emptyFields: [] };
    } catch (error: any) {
      apiErrorToast(error, 'Failed to save description sections');
      return { valid: false, words, emptyFields: [] };
    } finally {
      setSavingDescription(false);
    }
  };

  // ── Save single model row handler ───────────────────────────────────────────
  // Called per row — validates that row only, hits PUT /model once, returns
  // validation errors, api result (is_validated + execution_error), or null on API failure.
  const handleSaveModelRow = async (uid: string, index: number): Promise<{
    validationErrors?: string[];
    apiResult?: { is_validated: boolean; execution_error?: string | null };
  } | null> => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return null;
    }

    const row = normalizeModelRows(modelRows).find((r) => r._uid === uid);
    if (!row) return null;

    // Validate this row only
    const errors: string[] = [];
    if (!row.view_name.trim()) errors.push('View name is required');
    if (!row.grain.trim()) errors.push('Grain is required');
    if (!row.alias.trim()) errors.push('Alias is required');
    if (!row.primary_key.trim()) errors.push('Primary key is required');
    if (!row.sql_view.trim()) errors.push('SQL view is required');
    if (!(row.source_tables || []).length) errors.push('At least one source table is required');
    if (row.role === 'bridge') {
      if (!row.join_to_fact?.fact_key?.trim()) errors.push('Fact key is required for bridge tables');
      if (!row.join_to_fact?.this_key?.trim()) errors.push('Bridge key is required for bridge tables');
    }
    if (errors.length) return { validationErrors: errors };

    // Mark this specific row as saving
    setSavingModelRows((prev) => ({ ...prev, [uid]: true }));
    try {
      const result = await consoleDashboardsApi.updateDashboardModelRow({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        index,
        role: index === 0 ? 'fact' : 'bridge',
        view_name: row.view_name.trim().toLowerCase(),
        grain: row.grain.trim().toLowerCase(),
        alias: row.alias.trim().toLowerCase(),
        primary_key: row.primary_key.trim(),
        // sql_view: row.sql_view.trim(),
        sql_view: btoa(unescape(encodeURIComponent(row.sql_view.trim()))),
        source_tables: (row.source_tables || []).map((st) => ({
          table_id: st.table_id,
          table_name: st.table_name || tableById[st.table_id]?.postgres_table_name || '',
        })),
        join_to_fact:
          index > 0 && row.join_to_fact?.fact_key?.trim() && row.join_to_fact?.this_key?.trim()
            ? {
                fact_key: row.join_to_fact.fact_key.trim().toLowerCase(),
                this_key: row.join_to_fact.this_key.trim().toLowerCase(),
                type: (row.join_to_fact.type || 'exists').trim().toLowerCase(),
              }
            : null,
      });
      


      if (result.execution_error) {
        toast.error(`Execution error on ${index === 0 ? 'Fact' : `Bridge ${index}`}: check the error below`);
      } else {
        toast.success(`Row ${index === 0 ? 'Fact' : `Bridge ${index}`} saved`);
      }

      const apiResult = { is_validated: result.is_validated, execution_error: result.execution_error };

      // Fire in background — do NOT await here. Awaiting would cause hydrateFromDetail
      // to regenerate _uids before the form can store the execution_error against the current uid.
      void loadDashboardDetail(selectedDashboardId);

      return { apiResult };
    } catch (error: any) {
      apiErrorToast(error, `Failed to save row ${index}`);
      return null;
    } finally {
      setSavingModelRows((prev) => ({ ...prev, [uid]: false }));
    }
  };

  // ── Delete model row handler ─────────────────────────────────────────────────
  const handleDeleteModelRow = async (uid: string, index: number, viewName: string, role: string) => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    setDeletingModelRows((prev) => ({ ...prev, [uid]: true }));
    try {
      await consoleDashboardsApi.deleteDashboardModelRow({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        index,
        view_name: viewName,
        role,
      });
      toast.success(`Bridge ${index} removed`);
      await loadDashboardDetail(selectedDashboardId);
    } catch (error: any) {
      apiErrorToast(error, `Failed to remove bridge ${index}`);
    } finally {
      setDeletingModelRows((prev) => ({ ...prev, [uid]: false }));
    }
  };

  // ── Save manual construction description handler ─────────────────────────────
  const handleSaveManualConstructionDescription = async (
    viewName: string,
    viewColumn: string,
    description: string
  ): Promise<boolean> => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return false;
    }
    try {
      await consoleDashboardsApi.updateManualConstructionDescription({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        view_name: viewName,
        view_column: viewColumn,
        description,
      });
      toast.success('Description saved');
      return true;
    } catch (error: any) {
      apiErrorToast(error, 'Failed to save description');
      return false;
    }
  };

  // ── Generate columns derived handler ────────────────────────────────────────
  // Called per model row — hits POST /columns-derived-generation with that row's view_name
  const handleGenerateColumnsDerived = async (uid: string, viewName: string) => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    if (!viewName.trim()) {
      toast.error('Save the model row with a view name first');
      return;
    }
    setGeneratingColumnsByUid((prev) => ({ ...prev, [uid]: true }));
    try {
      const result = await consoleDashboardsApi.generateDashboardColumnsDerived({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        view_name: viewName,
      });
      setColumnsDerivedResults((prev) => ({ ...prev, [uid]: result }));
      toast.success(`Columns generated for ${viewName}`);
    } catch (error: any) {
      apiErrorToast(error, `Failed to generate columns for ${viewName}`);
    } finally {
      setGeneratingColumnsByUid((prev) => ({ ...prev, [uid]: false }));
    }
  };

  // ── Save authorized users handler ───────────────────────────────────────────
  const handleSaveAuthorizedUsers = async () => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    setSavingAuthorizedUsers(true);
    try {
      const emails = authorizedUsersInput
        .split('\n')
        .map((e) => e.trim())
        .filter(Boolean);
      await consoleDashboardsApi.updateDashboardAuthorizedUsers({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        authorized_users: emails,
      });
      await loadDashboardDetail(selectedDashboardId);
      toast.success('Authorized users saved');
    } catch (error: any) {
      apiErrorToast(error, 'Failed to save authorized users');
    } finally {
      setSavingAuthorizedUsers(false);
    }
  };

  // ── Save spec handler ────────────────────────────────────────────────────────
  // Description sections now have their own save button — this handles model,
  // columns derived, and authorized users only.
  const handleSaveSpec = async (event: FormEvent) => {
    event.preventDefault();
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }

    if ((name || '').trim().length < 3) {
      toast.error('Dashboard name must be at least 3 characters');
      return;
    }

    const normalizedRows = normalizeModelRows(modelRows);

    setSavingSpec(true);
    try {
      // ── 1. Save columns derived ──
      const viewsPayload = normalizedRows.map((row) => {
        const entries = columnsDerivedByUid[row._uid] || [];
        const tables = entries
          .filter((entry) => entry.table_name.trim() && entry.columns.length)
          .map((entry) => ({
            table_name: entry.table_name.trim(),
            columns: entry.columns.map((c) => String(c).trim()).filter(Boolean),
          }));
        return {
          view_name: row.view_name.trim(),
          tables,
        };
      }).filter((v) => v.view_name);

      await consoleDashboardsApi.updateDashboardColumnsDerived({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        views: viewsPayload,
      });

      // Reload fresh detail after all saves
      await loadDashboardDetail(selectedDashboardId);
      await loadDashboards();
      toast.success('Dashboard spec saved');
    } catch (error: any) {
      apiErrorToast(error, 'Failed to save dashboard spec');
    } finally {
      setSavingSpec(false);
    }
  };

  // ── SQL validation handler ───────────────────────────────────────────────────
  const handleValidateSql = async () => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    if (!sqlToValidate.trim()) {
      toast.error('Enter SQL query to validate');
      return;
    }
    
    setValidatingSql(true);
    try {
      const result = await consoleDashboardsApi.validateDashboardSql({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        sql: sqlToValidate,
      });
      setSqlResult(result);
      toast.success('SQL validated successfully');
    } catch (error: any) {
      apiErrorToast(error, 'Failed to validate SQL');
    } finally {
      setValidatingSql(false);
    }
  };

  // ── Approval handler ─────────────────────────────────────────────────────────
  const handleDashboardApproval = async (stage: 'developer' | 'admin') => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    if (stage === 'admin' && !user?.isAdmin) {
      toast.error('Admin role required');
      return;
    }
    setApprovingStage(stage);
    try {
      await consoleDashboardsApi.approveDashboard({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        stage,
        is_approved: true,
      });
      await loadDashboards();
      await loadDashboardDetail(selectedDashboardId);
      toast.success(stage === 'admin' ? 'Admin approval submitted' : 'Developer approval submitted');
    } catch (error: any) {
      apiErrorToast(error, 'Failed to submit approval');
    } finally {
      setApprovingStage('');
    }
  };


  const handlePushToProd = async () => {
    if (!requesterEmail || !selectedDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    setPushingToProd(true);
    try {
      await consoleDashboardsApi.pushDashboardToProd({
        requester_email: requesterEmail,
        dashboard_id: selectedDashboardId,
        enabled: true,
      });
      await loadDashboards();
      await loadDashboardDetail(selectedDashboardId);
      toast.success('Dashboard pushed to production');
    } catch (error: any) {
      apiErrorToast(error, 'Failed to push to production');
    } finally {
      setPushingToProd(false);
    }
  };

const handleIndexNow = async () => {
  if (!requesterEmail || !selectedDashboardId) {
    toast.error('Select a dashboard first');
    return;
  }
  setIndexingDashboard(true);
  try {
    await consoleDashboardsApi.indexDashboardNow({
      requester_email: requesterEmail,
      dashboard_id: selectedDashboardId,
    });
    toast.success('Dashboard indexed successfully');
  } catch (error: any) {
    apiErrorToast(error, 'Failed to index dashboard');
  } finally {
    setIndexingDashboard(false);
  }
};

// Handle sync dashboard access — hits POST /console/dashboards/{id}/sync-access
const handleAccessSync = async () => {
  if (!requesterEmail || !selectedDashboardId) {
    toast.error('Select a dashboard first');
    return;
  }
  setSyncingAccess(true);
  try {
    await consoleDashboardsApi.syncDashboardAccess({
      requester_email: requesterEmail,
      dashboard_id: selectedDashboardId,
    });
    toast.success('Access sync triggered');
  } catch (error: any) {
    apiErrorToast(error, 'Failed to trigger access sync');
  } finally {
    setSyncingAccess(false);
  }
};

  // ── Access guard ─────────────────────────────────────────────────────────────
  if (!user?.canAccessConsole) {
    return (
      <ConsoleLayout title="Dashboard">
        <div className="console-card">
          <p>You do not have Console access.</p>
        </div>
      </ConsoleLayout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <ConsoleLayout title="Dashboard">
      <div className="console-grid two-col">
        <DashboardSelectorCard
          dashboards={dashboards}
          selectedDashboardId={selectedDashboardId}
          setSelectedDashboardId={setSelectedDashboardId}
          loadingDashboards={loadingDashboards}
          loadDashboards={loadDashboards}
        />

        <DashboardApprovalCard
          selectedDashboard={selectedDashboard}
          isAdmin={!!user?.isAdmin}
          userRole={user?.role || ''}
          approvingStage={approvingStage}
          handleDashboardApproval={handleDashboardApproval}
          pushingToProd={pushingToProd}
          handlePushToProd={handlePushToProd}
        />
      </div>

      <DashboardSpecForm
        loadingDetail={loadingDetail}
        savingSpec={savingSpec}
        savingDescription={savingDescription}
        dashboardDetail={dashboardDetail}
        name={name}
        setName={setName}
        descriptionSections={descriptionSections}
        descriptionWords={descriptionWords}
        filtersApplicableInput={filtersApplicableInput}
        setFiltersApplicableInput={setFiltersApplicableInput}
        updateSection={updateSection}
        parseCsv={parseCsv}
        handleSaveDescriptionSections={handleSaveDescriptionSections}
        modelRows={modelRows}
        availableTables={availableTables}
        tableById={tableById}
        addBridgeRow={addBridgeRow}
        removeBridgeRow={removeBridgeRow}
        updateModelRow={updateModelRow}
        savingModelRows={savingModelRows}
        deletingModelRows={deletingModelRows}
        handleDeleteModelRow={handleDeleteModelRow}
        handleSaveModelRow={handleSaveModelRow}
        temporalScope={temporalScope}
        setTemporalScope={setTemporalScope}
        mainYearColumn={mainYearColumn}
        setMainYearColumn={setMainYearColumn}
        mainYearColumnOptions={mainYearColumnOptions}
        loadingMainColumns={loadingMainColumns}
        derivingTemporalScope={derivingTemporalScope}
        loadMainViewColumns={loadMainViewColumns}
        deriveTemporalScope={deriveTemporalScope}
        columnsDerivedByUid={columnsDerivedByUid}
        columnsDerivedResults={columnsDerivedResults}
        generatingColumnsByUid={generatingColumnsByUid}
        handleGenerateColumnsDerived={handleGenerateColumnsDerived}
        handleSaveManualConstructionDescription={handleSaveManualConstructionDescription}
        tableColumnsById={tableColumnsById}
        resolveTableIdFromReference={resolveTableIdFromReference}
        addDerivedEntry={addDerivedEntry}
        updateDerivedEntry={updateDerivedEntry}
        removeDerivedEntry={removeDerivedEntry}
        authorizedUsersInput={authorizedUsersInput}
        setAuthorizedUsersInput={setAuthorizedUsersInput}
        savingAuthorizedUsers={savingAuthorizedUsers}
        handleSaveAuthorizedUsers={handleSaveAuthorizedUsers}
        userRole={user?.role || ''}
        sourceLink={sourceLink}
        setSourceLink={setSourceLink}
        handleSaveSpec={handleSaveSpec}
        indexingDashboard={indexingDashboard}
        handleIndexNow={handleIndexNow}
        syncingAccess={syncingAccess}
        handleAccessSync={handleAccessSync}
      />

      {/* <SqlValidationCard
        sqlToValidate={sqlToValidate}
        setSqlToValidate={setSqlToValidate}
        validatingSql={validatingSql}
        selectedDashboardId={selectedDashboardId}
        handleValidateSql={handleValidateSql}
        sqlResult={sqlResult}
      /> */}

      {!dashboards.length && !loadingDashboards ? (
        <div className="console-card">
          <p>No dashboards assigned yet. Ask Admin to create one in the Admin tab.</p>
        </div>
      ) : null}
    </ConsoleLayout>
  );
};

export default ConsoleDashboard;